import { useCallback, useEffect, useRef, useState } from 'react';

import { PostcodeGeoJsonUrl } from '../CountryView/types';
import { loadPostcodePolygons } from '../CountryView/utils/loadPostcodePolygons';

interface UseUnifiedPolygonLoaderOptions {
  postcodeGeoJsonUrl: PostcodeGeoJsonUrl;
  activeCountry: string | null;
}

interface UseUnifiedPolygonLoaderReturn {
  polygonsByRegion: Record<string, GeoJSON.FeatureCollection>;
  loading: boolean;
  error: boolean;
  resolveUrl: (regionName: string, regionFeature?: GeoJSON.Feature) => string | null;
  loadForRegion: (
    regionName: string,
    regionFeature?: GeoJSON.Feature,
  ) => Promise<GeoJSON.FeatureCollection | null>;
  reset: () => void;
}

/**
 * Mirrors the legacy CountryView usePolygonLoader but with a country-aware
 * cache reset. Polygon FeatureCollections are cached by region name only;
 * because different countries can use the same region names ("North",
 * "Central"…), the persistent unified-map needs to drop the cache whenever
 * activeCountry changes — otherwise a stale ES file could surface inside CN.
 */
export function useUnifiedPolygonLoader({
  postcodeGeoJsonUrl,
  activeCountry,
}: UseUnifiedPolygonLoaderOptions): UseUnifiedPolygonLoaderReturn {
  const [polygonsByRegion, setPolygonsByRegion] = useState<
    Record<string, GeoJSON.FeatureCollection>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const lastCountryRef = useRef<string | null>(activeCountry);
  useEffect(() => {
    if (lastCountryRef.current === activeCountry) return;
    lastCountryRef.current = activeCountry;
    setPolygonsByRegion({});
    setLoading(false);
    setError(false);
  }, [activeCountry]);

  const resolveUrl = useCallback(
    (regionName: string, regionFeature?: GeoJSON.Feature): string | null => {
      if (!postcodeGeoJsonUrl) return null;
      if (typeof postcodeGeoJsonUrl === 'function') {
        return postcodeGeoJsonUrl(regionName, regionFeature);
      }
      return postcodeGeoJsonUrl;
    },
    [postcodeGeoJsonUrl],
  );

  const loadForRegion = useCallback(
    async (regionName: string, regionFeature?: GeoJSON.Feature) => {
      const url = resolveUrl(regionName, regionFeature);
      if (!url) {
        setLoading(false);
        setError(false);
        return null;
      }
      if (polygonsByRegion[regionName]) {
        return polygonsByRegion[regionName];
      }
      setLoading(true);
      setError(false);
      try {
        const data = await loadPostcodePolygons(url);
        setPolygonsByRegion((prev) => ({ ...prev, [regionName]: data }));
        setLoading(false);
        return data;
      } catch (err) {
        console.error(`Failed to load polygons for ${regionName}`, err);
        setLoading(false);
        setError(true);
        return null;
      }
    },
    [resolveUrl, polygonsByRegion],
  );

  const reset = useCallback(() => {
    setLoading(false);
    setError(false);
  }, []);

  return { polygonsByRegion, loading, error, resolveUrl, loadForRegion, reset };
}
